import { useState, useEffect } from 'react';
import { mormSigner, WasmStatus, MormSignerStatus } from '../sdk';

/**
 * Custom hook for managing MormSigner WASM initialization and status
 * 
 * @returns Object containing:
 *   - wasmStatus: Current WASM loading status
 *   - isReady: Boolean indicating if WASM is ready to use
 *   - status: Detailed MormSigner status information
 *   - retry: Function to retry WASM initialization
 */
export function useMormSigner() {
  const [wasmStatus, setWasmStatus] = useState<WasmStatus>({
    loading: true,
    loaded: false,
    error: null
  });

  const [status, setStatus] = useState<MormSignerStatus>({
    initialized: false,
    wasmLoaded: false,
    ready: false
  });

  // Initialize WASM on mount
  useEffect(() => {
    const initializeWasm = async (): Promise<void> => {
      try {
        setWasmStatus(prev => ({ ...prev, loading: true, error: null }));
        const success = await mormSigner.initialize();
        
        if (success) {
          // Check for version immediately
          let version = mormSigner.getVersion();
          
          // If version not found immediately, wait a bit and check again
          // (version might be set asynchronously by WASM module)
          if (!version) {
            await new Promise(resolve => setTimeout(resolve, 500));
            version = mormSigner.getVersion();
          }

          // Check one more time after a longer delay
          if (!version) {
            setTimeout(() => {
              const delayedVersion = mormSigner.getVersion();
              if (delayedVersion) {
                setWasmStatus(prev => ({ ...prev, version: delayedVersion }));
              }
            }, 2000);
          }
          
          setWasmStatus({ 
            loading: false, 
            loaded: true, 
            error: null,
            version: version 
          });
          setStatus(mormSigner.getStatus());
        } else {
          setWasmStatus({ 
            loading: false, 
            loaded: false, 
            error: 'Failed to load WASM module' 
          });
          setStatus(mormSigner.getStatus());
        }
      } catch (error) {
        setWasmStatus({ 
          loading: false, 
          loaded: false, 
          error: error instanceof Error ? error.message : 'Unknown error' 
        });
        setStatus(mormSigner.getStatus());
      }
    };

    initializeWasm();
  }, []);

  // Update status periodically to reflect changes
  useEffect(() => {
    if (wasmStatus.loaded) {
      // Check for version immediately and then periodically
      const checkVersion = () => {
        const version = mormSigner.getVersion();
        if (version && version !== wasmStatus.version) {
          setWasmStatus(prev => ({ ...prev, version }));
        }
      };

      // Check immediately
      checkVersion();

      // Then check periodically (version might be set asynchronously)
      const interval = setInterval(() => {
        setStatus(mormSigner.getStatus());
        checkVersion();
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [wasmStatus.loaded]);

  // Retry initialization
  const retry = async (): Promise<void> => {
    setWasmStatus({ loading: true, loaded: false, error: null });
    try {
      const success = await mormSigner.initialize();
      
      if (success) {
        const version = mormSigner.getVersion();
        setWasmStatus({ 
          loading: false, 
          loaded: true, 
          error: null,
          version: version 
        });
        setStatus(mormSigner.getStatus());
      } else {
        setWasmStatus({ 
          loading: false, 
          loaded: false, 
          error: 'Failed to load WASM module' 
        });
        setStatus(mormSigner.getStatus());
      }
    } catch (error) {
      setWasmStatus({ 
        loading: false, 
        loaded: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      setStatus(mormSigner.getStatus());
    }
  };

  return {
    wasmStatus,
    isReady: wasmStatus.loaded && status.ready,
    status,
    retry
  };
}
